/* eslint-disable @typescript-eslint/naming-convention,max-lines */

// external dependencies
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import { isEmpty, format, isValidDeeplink, getNestedValue } from "@swiggy-private/js-utils";
import { CTA, extractPlanPurchaseInfoFromCTAList } from "@swiggy-private/super-ui";
import { isItemAvailable } from "@swiggy-private/cart-ui";
// internal dependencies
import {
    getBridgeSdk,
    qFoodRestaurantsList,
    Q_FOOD_SLA,
    MIN_ANDROID_VERSION_Q_FOOD,
    MIN_IOS_VERSION_Q_FOOD,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    closeWebview,
    closeIxigoWebview,
    UpdateCartData,
    recordCustomTrackingNREvent,
    isIxigoFlow,
} from "@swiggy-private/checkout-core";
import {
    captureError,
    LocalStorage,
    LOCAL_STORAGE_KEYS,
    getFixedNotationIfNotInteger,
    isVersionCodeEligible,
    isAndroidPlatform,
    safeJsonParse,
    Storage,
    isMWebPlatform,
} from "@swiggy-private/core-ui";
import { CART_STATUS_CODES, BillDetailsTag } from "@swiggy-private/cart-ui";
import { LottieCartType } from "@cart-client/hooks/useCommonCartheaderLottie/constants";
import {
    Address,
    COUPON_LISTING_VERSION,
    EditAddressResponsePayload,
    NavigationScreenLinkTypes,
    NavigationTypes,
    STORAGE_TYPES,
} from "@swiggy-private/native-bridge";
import {
    UserAddress,
    CartItem as ICartItem,
    SubsItem,
    CouponMessageCard,
    RenderingDetails,
    UserSuperStatus,
    DeliveryInstructionDetail,
    BOOLEAN_AS_STRING,
    DeliveryInstructionCroutonDetail,
    TradeDiscountSuccessCard,
    ApiMenuCartItem,
    ApiCartItemParams,
    Location,
    TDSuccessCardType,
    IIrctcPayload,
    IIrctcTrainOrderAttributes,
    DeliverySpeedNudge,
    GroupOrderState,
    FreeDelCommsStyling,
    CouponModalIds,
    ICartSavedPreferences,
    CartItemWidgetRepositionType,
    CroutonIds,
    popupInfoSuccess,
    DELIVERY_SPEED,
    DISCOUNT_TYPE,
    AddressNudgePresentation,
    CartItemWidgetReposition,
    PorFlowXP,
    ICouponsAPIResponse,
    MenuItemsCalloutInfo,
    NativeLocalCart,
    ReviewedCartApiResponse,
    CartItem,
    GiftingCartApiResponse,
} from "@swiggy-private/cart-types";

import { buildCartJourneyContext } from "@cart-client/helpers/CartJourneyContext";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import {
    ReviewedCart,
    ApiCartItemAddons,
    ApiCartItemVariants,
    ApiMenuSubsItem,
    OFOWidgetDetails,
    OFOWidgetPosition,
    RawOrderItem,
} from "@cart-client/interfaces/Cart";

// apis
import { applyCoupon, removeCoupon, CrossSellBodyItem, removeCouponPublic } from "@cart-client/apis/Cart";

// constants
import {
    CART_ITEM_REWARD_TYPE,
    VALID_RENDERING_BUTTON_TYPES,
    FLOW_TYPE_OFO,
    EXPERIMENTS,
    CART_FEEDBACK_URL,
    IRCTC_ADDRESS_CHANGE_URL,
    PREVIOUS_CART_ID,
    DEFAULT_CART_ITEM_WIDGET_REPOSITION,
} from "@cart-client/constants/index";
import {
    DELIVERY_INSTRUCTIONS_ICONS,
    DIRECTIONS_TO_REACH,
    POP_DELIVERY_INSTRUCTIONS_ICONS,
    TOING_DELIVERY_INSTRUCTIONS_ICONS,
} from "@cart-client/constants/CartDeliveryInstruction";
import { DEFAULT_MAX_IOS_VERSION_FOR_FOOD_PAGE_REDIRECTION, SOCIAL_OFO_TEXT } from "@cart-client/constants/TextDict";
import { GROUP_ORDER_MENU_DEEPLINK } from "@cart-client/constants/GroupOrder";
import { SWGY_PWA_CART_DATA, WEB_FOOD_CART_LOCAL } from "@cart-client/constants/StorageKeys";

// Local imports
import { getHostFirstName } from "./GroupCartOrder";
import { getDeliverySpeedPresentationInfo } from "./CartDeliverySpeed";
import { applyCouponPublic } from "@cart-client/apis/publicCart";

export type ResultCartData = Result<ApiResponse<ReviewedCartApiResponse>, Error | ApiResponse>;
export type ResultCouponsData = Result<ICouponsAPIResponse, Error | ApiResponse>;

interface CartMetaDetails {
    orderComments?: string;
    cutleryComments?: string;
    optInNoContactDelivery?: boolean;
    optInBatchDelivery?: boolean;
}

interface CartItemsList {
    data: ICartItem | ApiMenuCartItem;
}

export interface LocalCartParams extends CartMetaDetails {
    isCouponValid: number;
    tipDetails: ReviewedCartApiResponse["tipDetails"] | null;
    cartMenuItems: ReviewedCartApiResponse["cart_menu_items"] | null;
    mealItems: ReviewedCartApiResponse["mealItems"] | null;
    couponCode?: string | null;
    discountMessage?: string | null | null;
}

// * Subset of CART_STATUS_CODES (packages/cart-ui/src/interfaces/CartStatus.ts) - Keys and codes must match
enum MENU_REFRESH_CART_STATUS_CODES {
    LOCATION_UNSERVICEABLE = CART_STATUS_CODES.LOCATION_UNSERVICEABLE,
    RESTAURANT_UNSERVICEABLE = CART_STATUS_CODES.RESTAURANT_UNSERVICEABLE,
    RESTAURANT_CLOSED = CART_STATUS_CODES.RESTAURANT_CLOSED,
    RESTAURANT_CLOSED_WITH_REOPEN_TODAY = CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_TODAY,
    RESTAURANT_CLOSED_WITH_REOPEN_TOMORROW = CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_TOMORROW,
    RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DAY = CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DAY,
    RESTAURANT_CLOSED_WITH_REOPEN_IN_MINUTES = CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_IN_MINUTES,
    RESTAURANT_CLOSED_WITH_NO_INFO = CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_NO_INFO,
    RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DATE = CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DATE,
    CITY_CLOSED = CART_STATUS_CODES.CITY_CLOSED,
    ITEM_OUTOFSTOCK = CART_STATUS_CODES.ITEM_OUTOFSTOCK,
    INCREASE_IN_DEMAND = CART_STATUS_CODES.INCREASE_IN_DEMAND,
    RAINS = CART_STATUS_CODES.RAINS,
    ZONE_NOT_OPEN = CART_STATUS_CODES.ZONE_NOT_OPEN,
    BLACKZONE = CART_STATUS_CODES.BLACKZONE,

    // IRCTC error status
    IRCTC_INVALID_PNR = CART_STATUS_CODES.IRCTC_INVALID_PNR,
    IRCTC_SEAT_NOT_CONFIRMED = CART_STATUS_CODES.IRCTC_SEAT_NOT_CONFIRMED,
    IRCTC_API_FAILURE = CART_STATUS_CODES.IRCTC_API_FAILURE,
    IRCTC_CUT_OFF_TIME_EXPIRED = CART_STATUS_CODES.IRCTC_CUT_OFF_TIME_EXPIRED,
    IRCTC_STATION_NOT_IN_JOURNEY = CART_STATUS_CODES.IRCTC_STATION_NOT_IN_JOURNEY,
    IRCTC_ORDER_WINDOW_LIMIT_ERROR = CART_STATUS_CODES.IRCTC_ORDER_WINDOW_LIMIT_ERROR,
}

export function hasValidCoupon(reviewedCartResponse: ReviewedCartApiResponse | null): number {
    return reviewedCartResponse?.is_coupon_valid ?? 0;
}

export function getTipDetails(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["tipDetails"] | undefined {
    return reviewedCartResponse?.tipDetails ?? undefined;
}

export function getDropOffInstructions(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["drop_off_instructions"] | undefined {
    return reviewedCartResponse?.drop_off_instructions ?? undefined;
}

export function getIrctcData(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["irctc_data"] | undefined {
    return reviewedCartResponse?.irctc_data ?? undefined;
}

export function getIncognito(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["is_incognito_cart"] | undefined {
    return reviewedCartResponse?.is_incognito_cart ?? undefined;
}

export function getCartSavedPreferences(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["cart_saved_preferences"] | {} {
    return reviewedCartResponse?.cart_saved_preferences ?? {};
}

export function isCartContainBoltItems(reviewedCartResponse: ReviewedCartApiResponse | null | undefined): boolean {
    return reviewedCartResponse?.is_bolt_cart ?? false;
}

export function getSelectedDropOffInstructionIds(
    instructions: DeliveryInstructionDetail[] | undefined,
): string[] | undefined {
    if (!isEmpty(instructions)) {
        const filteredIds = instructions
            .filter((item: DeliveryInstructionDetail) => item.selected && item.instruction_type !== DIRECTIONS_TO_REACH)
            .map((item: DeliveryInstructionDetail) => {
                return item.id;
            });
        return filteredIds;
    }
    return undefined;
}

export const getCartMenuItemPropertyForUpdate = ({
    menu_item_id,
    addons,
    variants,
    in_stock,
    quantity,
    rewardType,
    freebieType,
    item_offer_tags,
    final_price,
    base_price,
    defaultPrice,
    price,
    subtotal,
    isVersion2,
    costV2,
    subtotal_trade_discount,
    valid_addons,
    valid_variants,
    valid_variants_v2,
    item_price_strike_off,
    default_variant_combination_final_price,
    name,
    cloudinaryImageId,
    offer_ids,
    item_offer_cart_meta,
    customer_request_messages,
    cart_context,
}: ApiMenuCartItem): Pick<ICartItem, ApiCartItemParams> => ({
    menu_item_id,
    addons,
    variants,
    in_stock,
    quantity,
    rewardType,
    freebieType,
    item_offer_tags,
    final_price,
    base_price,
    defaultPrice,
    price,
    subtotal,
    isVersion2,
    costV2,
    subtotal_trade_discount,
    valid_addons,
    valid_variants,
    valid_variants_v2,
    item_price_strike_off,
    default_variant_combination_final_price,
    name,
    cloudinaryImageId,
    offer_ids,
    item_offer_cart_meta,
    customer_request_messages,
    cart_context,
});

export function getCartMenuItems(reviewedCartResponse: ReviewedCartApiResponse | null): ApiMenuCartItem[] | undefined {
    const menuItem: ApiMenuCartItem[] = [];
    if (reviewedCartResponse?.cart_menu_items && reviewedCartResponse.cart_menu_items.length > 0) {
        reviewedCartResponse.cart_menu_items.map((item) => {
            menuItem.push(getCartMenuItemPropertyForUpdate(item));
        });
    }
    return menuItem;
}

export function getMealItems(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["mealItems"] | undefined {
    return reviewedCartResponse?.mealItems ?? undefined;
}

export function getCrossSellSuggestion(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["crossSellingSuggestion"] | undefined {
    return reviewedCartResponse?.crossSellingSuggestion;
}

export function getCouponCode(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["coupon_code"] | undefined {
    if (Object.hasOwnProperty.call(reviewedCartResponse?.coupon_details || {}, "coupon_codes")) {
        return reviewedCartResponse?.coupon_details?.coupon_codes?.restaurant_coupon;
    }
    return reviewedCartResponse?.coupon_code;
}

export function getDiscountMessage(
    reviewedCartResponse: ReviewedCartApiResponse | null,
): ReviewedCartApiResponse["discount_message"] | undefined {
    return reviewedCartResponse?.discount_message ?? undefined;
}

export function getSubsItems(reviewedCartResponse: ReviewedCartApiResponse | null): ApiMenuSubsItem[] | undefined {
    const subsItems: ApiMenuSubsItem[] = [];
    if (reviewedCartResponse?.subscription_items && reviewedCartResponse.subscription_items.length > 0) {
        reviewedCartResponse.subscription_items.map((item) => {
            subsItems.push({ plan_id: item.plan_id, quantity: item.quantity });
        });
    }
    return subsItems;
}

export function getSelectedAddressFromReviewedCart(cartData: ReviewedCartApiResponse): UserAddress | null {
    let selectedAddress = null;

    // return if no addresses
    if (!cartData.addresses || !(cartData.addresses.length > 0)) {
        return null;
    }

    cartData.addresses.map((address) => {
        if (address.selected) {
            selectedAddress = address;
        }
    });

    return selectedAddress;
}

interface NativeCartParamsForUpdateResponse {
    orderComment?: string;
    cutleryComments?: string;
    cartLatLong?: Location;
    sld?: boolean;
    userLiveLat?: string | number;
    userLiveLng?: string | number;
    date?: string;
    is_membership_plan_removed?: boolean;
    is_incognito_cart?: boolean;
    cart_saved_preferences?: ICartSavedPreferences;
    p_segment_id?: string;
    parent_restaurant_id?: string;
    cart_initialization_source?: string;
    cart_category_type?: string;
    /**
     * Portal is a pass-through between app and backend for these fields.
     * These fields are not being consumed anywhere in the portal code.
     */
    cart_journey_ctx?: {
        cart_initialization_source?: string;
        cart_category_type?: string;
        request_contexts?: string[];
        additional_context?: { [key: string]: string };
    };
    /**
     * This will be populated iff delivery speed selection is updated by user click
     */
    customer_selected_sla_option?: string;
    flowContext?: {
        type: string;
        start_time: number;
        end_time: number;
        offset_time: number;
    };
}

export function prepareNativeCartParamsFromReviewedCartResponse(
    cartData: ReviewedCartApiResponse,
    localCartData: NativeCartParamsForUpdateResponse = {},
): NativeLocalCart {
    const isCouponValid = hasValidCoupon(cartData);
    const tipDetails = getTipDetails(cartData);
    const cartItems = getCartMenuItems(cartData);
    const mealItems = getMealItems(cartData);
    const subscriptionItems = getSubsItems(cartData);
    const couponCode = getCouponCode(cartData);
    const discountMessage = getDiscountMessage(cartData);
    const crossSellingSuggestion = getCrossSellSuggestion(cartData);
    const selectedAddress = getSelectedAddressFromReviewedCart(cartData);
    const dropOffInstructions = getDropOffInstructions(cartData);
    const irctc_data = getIrctcData(cartData);
    const is_incognito_cart = getIncognito(cartData);
    const cart_saved_preferences = getCartSavedPreferences(cartData);
    const selectedDropOffInstructioIds = getSelectedDropOffInstructionIds(dropOffInstructions);
    const nativeCartParams: NativeLocalCart = {
        restaurantId: cartData.restaurant_details?.id,
        restaurantName: cartData.restaurant_details?.name,
        restaurantCloudinaryImageId: cartData.restaurant_details?.cloudinary_image_id,
        originalCartId: cartData.originalCartId,
        address_id: cartData.address_id,
        p_segment_id: cartData.p_segment_id ?? localCartData?.p_segment_id ?? "",
        parent_restaurant_id: cartData.parent_restaurant_id ?? localCartData?.parent_restaurant_id ?? "",
        isCouponValid,
        tipDetails,
        cartItems,
        subscriptionItems,
        mealItems,
        couponCode,
        discountMessage,
        irctc_data,
        is_incognito_cart,
        cart_saved_preferences: cart_saved_preferences ?? {},
        cross_selling_suggestion: crossSellingSuggestion,
        cart_id: cartData.cartId,
        orderComment: localCartData.orderComment || "",
        cutleryComments: localCartData.cutleryComments || "",
        cartLatLong: localCartData.cartLatLong,
        sld: localCartData.sld,
        userLiveLat: localCartData.userLiveLat,
        userLiveLng: localCartData.userLiveLng,
        date: localCartData.date,
        selected_sla_option: selectedAddress?.selected_sla_option || "",
        delivery_type_opt_in: selectedAddress?.delivery_type_opt_in,
        is_membership_plan_removed: localCartData.is_membership_plan_removed,
        cart_initialization_source: localCartData?.cart_initialization_source,
        cart_category_type: localCartData?.cart_category_type,
        cart_journey_ctx: buildCartJourneyContext(localCartData?.cart_journey_ctx, cartData),
        customer_selected_sla_option: localCartData?.customer_selected_sla_option || "",
        flowContext: localCartData?.flowContext,
    };

    if (selectedDropOffInstructioIds) {
        nativeCartParams.selected_drop_off_instruction_ids = selectedDropOffInstructioIds;
    }

    if (localCartData.sld) {
        nativeCartParams.sldRestaurantId = cartData.restaurant_details?.id;
    }

    if (selectedAddress !== null) {
        nativeCartParams.selectedAddress = selectedAddress;
    }

    return nativeCartParams;
}

export function prepareNativeCartParamsFromOrderDetails(orderDetails: RawOrderItem): NativeLocalCart {
    const cartItems = orderDetails?.order_items.map((item: ICartItem) => {
        const cartItem = {
            menu_item_id: Number(item?.item_id),
            name: item?.name,
            addons: item?.addons,
            in_stock: item?.in_stock,
            variants: item?.variants,
            quantity: Number(item?.["quantity"]) || 0,
            rewardType: item?.rewardType,
            freebieType: item?.freebieType,
            item_offer_tags: item?.item_offer_tags,
            final_price: item?.final_price,
            base_price: item?.base_price,
            defaultPrice: item?.defaultPrice,
            price: item?.price,
            subtotal: item?.subtotal,
            isVersion2: item?.isVersion2,
            costV2: Number(item?.["costV2"]) || 0,
            subtotal_trade_discount: item?.subtotal_trade_discount,
            valid_addons: item?.valid_addons,
            valid_variants: item?.valid_variants,
            valid_variants_v2: item?.valid_variants_v2,
            item_price_strike_off: item?.item_price_strike_off,
            default_variant_combination_final_price: item?.default_variant_combination_final_price,
            cloudinaryImageId: item?.cloudinaryImageId,
        };
        return cartItem;
    });

    const localCart: NativeLocalCart = {
        restaurantId: orderDetails?.restaurant_id,
        restaurantName: orderDetails?.restaurant_name,
        restaurantCloudinaryImageId: orderDetails?.restaurant_cover_image,
        cartItems,
        address_id: orderDetails?.delivery_address?.id,
        selected_sla_option: orderDetails?.selected_sla_option,
        p_segment_id: "",
        parent_restaurant_id: "",
    };
    return localCart;
}

export const canShowCutleryInstruction = (cartData?: ReviewedCartApiResponse | null): boolean =>
    cartData?.configurations?.show_cutlery_instruction ?? false;

export const getCartItemWidgetReposition = (cartData?: ReviewedCartApiResponse | null): CartItemWidgetReposition[] =>
    cartData?.cartItemWidgetReposition ?? DEFAULT_CART_ITEM_WIDGET_REPOSITION; //TODO: Post XP Scale up we should remove the default handling

export const canShowCutleryOptInWidget = (cartData?: ReviewedCartApiResponse | null): boolean => {
    if (isEmpty(cartData)) {
        return false;
    }

    const cartItemWidgetReposition = getCartItemWidgetReposition(cartData);

    if (isEmpty(cartItemWidgetReposition)) {
        return false;
    }

    const hasCutleryWidget = cartItemWidgetReposition.some(
        (item: CartItemWidgetReposition) => item?.type === CartItemWidgetRepositionType.SEND_CUTLERY,
    );

    return hasCutleryWidget && (cartData.configurations?.show_cutlery_opt_in_widget ?? false);
};

export const canShowRestaurantDetails = (cartData: ReviewedCartApiResponse | null): boolean =>
    cartData?.restaurant_details !== undefined;

export const isCartEmpty = (cartData: ReviewedCartApiResponse | null): boolean =>
    (cartData?.cart_menu_items?.length ?? 0) === 0 && (cartData?.mealItems?.length ?? 0) === 0;

export const getCouponErrorCard = (
    cartData?: ReviewedCartApiResponse | GiftingCartApiResponse | null,
): CouponMessageCard | undefined => cartData?.coupon_details?.coupon_error_card;

export const getCouponSuccessCard = (cartResponse: ReviewedCart | null): CouponMessageCard | undefined =>
    cartResponse?.data?.coupon_details?.coupon_success_card;

export const getCouponAutoAppliedFlag = (cartResponse: ReviewedCart | null): boolean =>
    cartResponse?.data?.coupon_details?.is_coupon_auto_applied === true;

export const getTradeDiscountSuccessCard = (cartResponse: ReviewedCart | null): TradeDiscountSuccessCard | undefined =>
    cartResponse?.data?.trade_discount_success_card?.td_success_card_type !==
    TDSuccessCardType.OFFER_CARD_TYPE_VALUE_MEALS
        ? cartResponse?.data?.trade_discount_success_card
        : undefined;

export const getEcoSaverToastData = (cartResponse: ReviewedCart | null): popupInfoSuccess | null => {
    const widgetInfo = getDeliverySpeedPresentationInfo(cartResponse?.data);

    const ecoSaverOption = widgetInfo?.delivery_speed_widget_info?.delivery_speed_options?.find(
        (option) => option.delivery_speed === DELIVERY_SPEED.BATCH_NO_RUSH,
    );

    const popup_info = ecoSaverOption?.popup_info;

    if (isEmpty(popup_info) || isEmpty(popup_info?.message)) return null;

    return {
        icon: popup_info.icon,
        message: popup_info.message,
        ctaText: popup_info.ctaText,
        lottie_id: popup_info.lottie_id,
        discountType: ecoSaverOption?.discount_type as DISCOUNT_TYPE,
    };
};

export const getFreebiePopupInfo = (cartResponse: ReviewedCart | null): TradeDiscountSuccessCard | undefined =>
    cartResponse?.data?.trade_discount_success_card?.td_success_card_type ===
    TDSuccessCardType.OFFER_CARD_TYPE_FREEBIE_OFFER
        ? cartResponse?.data?.trade_discount_success_card
        : undefined;

export const getTradeDiscountCouponCardUI = (
    cartResponse: ReviewedCart | null,
): TradeDiscountSuccessCard | undefined =>
    cartResponse?.data?.trade_discount_success_card?.td_success_card_type ===
    TDSuccessCardType.OFFER_CARD_TYPE_VALUE_MEALS
        ? cartResponse?.data?.trade_discount_success_card
        : undefined;

export const getTradeDiscountRewardAmount = (cartResponse: ReviewedCart | null): number | undefined =>
    cartResponse?.data?.trade_discount_reward_amount;

export const isCouponAppliedSuccessfully = (cartData: ReviewedCartApiResponse | null): boolean =>
    !isEmpty(cartData?.coupon_details?.coupon_success_card);

export const isCouponNotApplied = (cartData: ReviewedCartApiResponse | null): boolean =>
    !isEmpty(cartData?.coupon_details?.coupon_error_card);

export const isTradeDiscountCouponActive = (enabledOverlayId: string) =>
    enabledOverlayId === CouponModalIds.TRADE_DISCOUNT_COUPON;

export const isPocketHeroCashbackActive = (enabledOverlayId: string) =>
    enabledOverlayId === CouponModalIds.POCKET_HERO_CASHBACK;

export const isExpiryCashbackActive = (enabledOverlayId: string) => enabledOverlayId === CouponModalIds.EXPIRY_CASHBACK;

export const isInstantCouponActive = (enabledOverlayId: string) => enabledOverlayId === CouponModalIds.INSTANT_COUPON;

export const isDealRushActive = (enabledOverlayId: string) => enabledOverlayId === CouponModalIds.DEAL_RUSH;

export const isGenericDelightPopupActive = (enabledOverlayId: string) =>
    enabledOverlayId === CouponModalIds.GENERIC_DELIGHT_POPUP;
export const isEcoSaverCroutonActive = (enabledOverlayId: string) =>
    enabledOverlayId === CroutonIds.FREE_DEL_AND_CASHBACK_TOAST;

export const getDiscountType = (cartData?: ReviewedCartApiResponse | null): string =>
    cartData?.discount_message_type ?? "";

export const getTotalSavings = (cartData?: ReviewedCartApiResponse | null): number => {
    const totalSavings = (cartData?.discount_total ?? 0) + (cartData?.free_delivery_discount ?? 0);
    return Number(getFixedNotationIfNotInteger(totalSavings));
};

export async function updateApplyCoupon(
    apiHost: string,
    couponCode: string,
    enableApplyCouponPE = false,
): Promise<ResultCartData> {
    const getApplyCoupon = enableApplyCouponPE ? applyCouponPublic : applyCoupon;
    return getApplyCoupon(apiHost, couponCode)
        .then((data) => {
            if (!data) {
                throw new Error("Received empty data from cart details");
            }

            return Ok(data);
        })
        .catch((err) => {
            recordCustomTrackingNREvent({
                action: "update_apply_coupon",
                errorStack: err,
                couponCode: couponCode || "",
                timestamp: Date.now(),
                sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
            });
            return Err(err) as ResultCartData;
        });
}

export async function updateRemoveCoupon(
    apiHost: string,
    enableRemoveCouponPE: boolean,
    couponCode?: string,
): Promise<ResultCartData> {
    const removeCouponFn = enableRemoveCouponPE ? removeCouponPublic : removeCoupon;
    return removeCouponFn(apiHost, couponCode)
        .then((data) => {
            if (!data) {
                throw new Error("Received empty data from cart details");
            }

            return Ok(data);
        })
        .catch((err) => {
            recordCustomTrackingNREvent({
                action: "remove_coupon",
                errorStack: err,
                couponCode: couponCode || "",
                timestamp: Date.now(),
                sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
            });
            return Err(err) as ResultCartData;
        });
}

export const isValidCartAddress = (address: UserAddress): boolean => address.delivery_valid === 1;

export function hasValidSavedAddresses(addressList: ReviewedCartApiResponse["addresses"] = []): boolean {
    let isValid = false;
    addressList.map((address) => {
        if (isValidCartAddress(address)) {
            isValid = true;
        }
    });
    return isValid;
}

export function hasSelectedDeliveryAddress(
    addressList: ReviewedCartApiResponse["addresses"] = [],
    addressId: string,
): boolean {
    let hasSelectedAddress = false;
    let validAddressCount = 0;
    addressList.map((address) => {
        if (isValidCartAddress(address)) {
            validAddressCount += 1;
            if (addressId === address.id) {
                hasSelectedAddress = true;
            }
        }
    });

    // If only one valid delivery address, treat that as selected address
    return hasSelectedAddress || validAddressCount === 1;
}

export function validDeliveryAddressList(addressList: ReviewedCartApiResponse["addresses"] = []): UserAddress[] {
    return addressList.filter((address) => address.delivery_valid === 1);
}

export function getSelectedAddress(
    addressList: ReviewedCartApiResponse["addresses"] = [],
    addressId: ReviewedCartApiResponse["address_id"],
): UserAddress | null {
    if (!addressList) {
        return null;
    }

    const validAddressList = validDeliveryAddressList(addressList);
    if (validAddressList.length === 1) {
        return validAddressList[0];
    }

    return addressList.find((ad) => ad.id === addressId) ?? null;
}

export function hasCustomisedSameVariantsAddons(
    item: ApiMenuCartItem,
    addons: { choice_id: string | number }[] = [],
    variants: { variation_id: string | number }[] = [],
): boolean {
    if (item.addons.length !== addons.length || item.variants.length !== variants.length) {
        return false;
    }

    const responseAddons: (string | number)[] = [];
    const responseVariants: (string | number)[] = [];
    const userAddons: (string | number)[] = [];
    const userVariants: (string | number)[] = [];

    addons.map((addon) => responseAddons.push(String(addon.choice_id)));
    item.addons.map((addon) => userAddons.push(String(addon.choice_id)));
    variants.map((variant) => responseVariants.push(String(variant.variation_id)));
    item.variants.map((variant) => userVariants.push(String(variant.variation_id)));

    let hasSameVairantsAddons = true;

    responseAddons.map((addonId) => {
        if (userAddons.indexOf(addonId) == -1) {
            hasSameVairantsAddons = false;
        }
    });
    responseVariants.map((variantId) => {
        if (userVariants.indexOf(variantId) == -1) {
            hasSameVairantsAddons = false;
        }
    });

    return hasSameVairantsAddons;
}

export function getMenuItemsWithSameId(cartItems: ApiMenuCartItem[] = [], menuItemId: number): ApiMenuCartItem[] {
    return cartItems.filter((cartItem) => cartItem.menu_item_id === menuItemId);
}

export function sameMenuItemWithAddonsNVariantsExist(
    cartItems: ApiMenuCartItem[] = [],
    addons: ApiCartItemAddons[] = [],
    variants: ApiCartItemVariants[] = [],
): boolean {
    let result = false;
    cartItems.map((cartItem) => {
        if (hasCustomisedSameVariantsAddons(cartItem, addons, variants)) {
            result = true;
        }
    });
    return result;
}

export function getItemWithSameAddonsNVariants(
    cartItems: ApiMenuCartItem[] = [],
    addons: ApiCartItemAddons[] = [],
    variants: ApiCartItemVariants[] = [],
): ApiMenuCartItem | null {
    let item: ApiMenuCartItem | null = null;
    cartItems.map((cartItem) => {
        if (hasCustomisedSameVariantsAddons(cartItem, addons, variants)) {
            item = cartItem;
        }
    });
    return item;
}

export function isItemWithSameAddonsNVariants(
    cartItem: ApiMenuCartItem,
    addons: ApiCartItemAddons[] = [],
    variants: ApiCartItemVariants[] = [],
): boolean {
    if (cartItem.addons.length !== addons.length || cartItem.variants.length !== variants.length) {
        return false;
    }

    const responseAddons: (string | number)[] = [];
    const responseVariants: (string | number)[] = [];
    const userAddons: (string | number)[] = [];
    const userVariants: (string | number)[] = [];

    addons.map((addon) => responseAddons.push(String(addon.choice_id)));
    cartItem.addons.map((addon) => userAddons.push(String(addon.choice_id)));
    variants.map((variant) => responseVariants.push(String(variant.variation_id)));
    cartItem.variants.map((variant) => userVariants.push(String(variant.variation_id)));

    let hasSameVairantsAddons = true;

    responseAddons.map((addonId) => {
        if (userAddons.indexOf(addonId) == -1) {
            hasSameVairantsAddons = false;
        }
    });
    responseVariants.map((variantId) => {
        if (userVariants.indexOf(variantId) == -1) {
            hasSameVairantsAddons = false;
        }
    });

    return hasSameVairantsAddons;
}

export function getMenuItems(cartResponse: ReviewedCart): ICartItem[] {
    return cartResponse.data?.cart_menu_items ?? [];
}

export function getSubscriptionItems(cartResponse: ReviewedCart | null): SubsItem[] {
    return cartResponse?.data?.subscription_items ?? [];
}

export function getUserSuperStatus(cartResponse: ReviewedCart | null): UserSuperStatus | undefined {
    return cartResponse?.data?.user_super_status_details?.user_status;
}

export function isSuperV3Nudge(itemData?: RenderingDetails): boolean {
    return itemData?.key?.toUpperCase() === VALID_RENDERING_BUTTON_TYPES.SUPER_V3_NUDGE;
}

export function getFreebieItems(cartResponse: ReviewedCart): ICartItem[] {
    const menuItems = cartResponse.data?.cart_menu_items ?? [];

    return menuItems.filter((item) => item.rewardType === CART_ITEM_REWARD_TYPE.FREEBIE);
}

const callOpenSuperV3 = (link: string): Promise<UpdateCartData["subscriptionItems"]> => {
    try {
        return getBridgeSdk()
            .openSuperV3({ link })
            .then((response) => {
                if (response?.planId) {
                    return [
                        {
                            plan_id: response.planId,
                            quantity: 1,
                        },
                    ];
                }
            })
            .catch(() => void 0);
    } catch (err) {
        captureError(err);
    }
    return Promise.resolve([]);
};

export const getSuperSubscriptionDetails = async (
    itemData?: RenderingDetails,
): Promise<UpdateCartData["subscriptionItems"]> => {
    const cta = extractPlanPurchaseInfoFromCTAList(itemData?.meta?.cta, itemData?.meta?.ctas as CTA[]);
    const link = cta?.link;
    if (link) {
        return callOpenSuperV3(link);
    }
    return [];
};

export const changeSuperSubscriptionDetails = async (link: string): Promise<UpdateCartData["subscriptionItems"]> => {
    if (link) {
        return callOpenSuperV3(link);
    }
    return [];
};

export const getAvailableCartItems = (cartItems: ICartItem[]): ICartItem[] => {
    if (!cartItems || cartItems.length < 1) {
        return [] as ICartItem[];
    }

    return cartItems.filter((item) => isItemAvailable(item));
};

export const getUnServiceableItemsCount = (cartResponse: ReviewedCart): number | undefined => {
    const cartItems = cartResponse.data?.cart_menu_items || [];
    if (!cartItems || cartItems.length < 1) {
        return undefined;
    }

    let unServiceableItemsCount = 0;

    cartItems.map((item) => {
        if (!isItemAvailable(item)) {
            unServiceableItemsCount += 1;
        }
    });

    return unServiceableItemsCount === 0 ? undefined : unServiceableItemsCount;
};

export const getCartItemsCount = (cartResponse: ReviewedCart): number => {
    const cartItems = cartResponse.data?.cart_menu_items || [];
    const mealItems = cartResponse.data?.mealItems || [];

    let cartItemsCount = 0;
    cartItems.map((item) => (cartItemsCount += item.quantity));

    return cartItemsCount + mealItems.length;
};

export const getRestaurantName = (cartResponse: ReviewedCart): string | undefined => {
    return cartResponse.data?.restaurant_details?.name;
};

export const getCartId = (cartResponse: ReviewedCart | null): string => {
    return String(cartResponse?.data?.cartId ?? "");
};

export const getOriginalCartId = (cartResponse: ReviewedCart): string => {
    return String(cartResponse?.data?.originalCartId ?? "");
};

export const getRestaurantId = (cartResponse: ReviewedCart | null): string => {
    return String(cartResponse?.data?.restaurant_details?.id ?? "");
};

export const getSessionId = (): string => {
    return String(GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "");
};

export const getTipAmount = (cartResponse: ReviewedCart): number | undefined => {
    return cartResponse.data?.tipDetails?.amount ?? undefined;
};

const getDeliveryInstructionsIcon = (isPopCart?: boolean, isToingCart?: boolean) => {
    if (isPopCart) {
        return POP_DELIVERY_INSTRUCTIONS_ICONS;
    }
    if (isToingCart) {
        return TOING_DELIVERY_INSTRUCTIONS_ICONS;
    }
    return DELIVERY_INSTRUCTIONS_ICONS;
};

export const getDeliveryInstructionData = (
    cartResponse: ReviewedCart | null,
    isPopCart?: boolean,
    isToingCart?: boolean,
): DeliveryInstructionDetail[] | undefined => {
    /* TODO: Remove this once the backend deployment includes the correct icon URL's */
    if (isEmpty(cartResponse?.data?.drop_off_instructions)) {
        return;
    }

    const deliveryInstructionIcon = getDeliveryInstructionsIcon(isPopCart, isToingCart);
    const isMWeb = isMWebPlatform(GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform));
    const updatedInstructions = cartResponse?.data?.drop_off_instructions
        ?.filter((instruction) => {
            // Only filter out INSTRUCTION_TYPE_REACH for mweb platform
            // TODO: Remove this and make it BE driven in FF
            return !(isMWeb && instruction.instruction_type === DIRECTIONS_TO_REACH);
        })
        ?.map((instruction) => {
            const iconUrls = deliveryInstructionIcon[instruction.id as keyof typeof deliveryInstructionIcon];

            if (iconUrls) {
                return {
                    ...instruction,
                    active_icon_url_id: iconUrls.active,
                    inactive_icon_url_id: iconUrls.inactive,
                };
            }
            return instruction;
        });

    return updatedInstructions;
};

export const getDeliveryInstructionCroutonData = (
    cartResponse: ReviewedCart | null,
): DeliveryInstructionCroutonDetail | undefined => {
    return cartResponse?.data?.delivery_instructions_crouton;
};

export const getExperimentValue = (cartResponse: ReviewedCart | null, key: string): unknown | undefined => {
    return cartResponse?.data?.XPData?.[key];
};

export const getFeedbackUrl = (): string => {
    if (typeof window !== "undefined") {
        return window?.__CART_FEEDBACK_URL__ || CART_FEEDBACK_URL;
    }

    return CART_FEEDBACK_URL;
};

export const getIrctcAddressChangeUrl = (): string => {
    if (typeof window !== "undefined") {
        return window?.__IRCTC_ADDRESS_CHANGE_URL__ || IRCTC_ADDRESS_CHANGE_URL;
    }

    return IRCTC_ADDRESS_CHANGE_URL;
};

export const getSavingsBannerData = (
    cartResponse: ReviewedCart | GiftingReviewedCart | null,
): ReviewedCartApiResponse["discount_message_v2"] | undefined => {
    if (
        !isEmpty(cartResponse as GiftingReviewedCart) &&
        !isEmpty((cartResponse as GiftingReviewedCart)?.data?.savings_message)
    ) {
        return {
            title: (cartResponse as GiftingReviewedCart)?.data?.savings_message ?? "",
            subtitle: "",
            message_details: {},
        };
    }
    if (!isEmpty(cartResponse) && !isEmpty(cartResponse.data)) {
        return cartResponse.data.discount_message_v2;
    }
};

export const areSwiggyOneBenefitsApplicable = (cartResponse: ReviewedCart | null): boolean => {
    return !!cartResponse?.data?.swiggyOneBenefitsApplicable;
};

export const getBillViewChevronTag = (cartResponse: ReviewedCart | null): string | undefined => {
    if (isEmpty(cartResponse)) {
        return undefined;
    }

    if (!isEmpty(cartResponse.data)) {
        const cartType = cartResponse.data?.cart_type;
        if (cartType === LottieCartType._99STORE) {
            return BillDetailsTag._99STORE;
        }

        if (cartResponse?.data?.is_swiggy_black_applicable) {
            return BillDetailsTag.BLCK;
        }

        if (cartResponse?.data?.swiggyOneBenefitsApplicable) {
            return BillDetailsTag.ONE;
        }
    }
    return undefined;
};

export const getOFOWidgetDetails = (
    address: UserAddress | null,
    flowType?: string,
    isAwayLocation?: boolean,
): OFOWidgetDetails => {
    if (!address) return {};
    if (flowType?.toLowerCase() === FLOW_TYPE_OFO && address?.name && address?.mobile) {
        return {
            titleOFOWidget: SOCIAL_OFO_TEXT.editTitle.replace(":name", address.name),
            subTitleOFOWidget: SOCIAL_OFO_TEXT.editSubTitle.replace(":mobile", address.mobile),
            actionTextOFOWidget: SOCIAL_OFO_TEXT.editAction,
            showGiftIcon: true,
        };
    } else if (isAwayLocation) {
        return {
            titleOFOWidget: SOCIAL_OFO_TEXT.addPlusTitle,
            actionTextOFOWidget: SOCIAL_OFO_TEXT.changeAction,
        };
    } else if (isAwayLocation === false) {
        // Permanent OFO Widget Case
        return {
            titleOFOWidget: SOCIAL_OFO_TEXT.addPlusTitle,
            subTitleOFOWidget: SOCIAL_OFO_TEXT.addDetailsSubTitle,
            actionTextOFOWidget: SOCIAL_OFO_TEXT.changeAction,
            showTitleAboveWidget: true,
            widgetPosition: OFOWidgetPosition.BOTTOM,
        };
    }
    return {};
};

export const isAwayLocationWithoutOFO = (reviewedCart: ReviewedCart | null): boolean =>
    !!(reviewedCart?.data?.is_away_location && reviewedCart?.data?.user_flow_info?.flow_type !== FLOW_TYPE_OFO);

export const getIsAwayLocation = (reviewedCart: ReviewedCart | null): boolean | undefined =>
    reviewedCart?.data?.is_away_location;

export const extractOFOAddressFromEditAddressNB = (response?: EditAddressResponsePayload): Address | undefined => {
    const platformFromHeader = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    if (response?.address && isAndroidPlatform(platformFromHeader) && typeof response?.address === "string") {
        return safeJsonParse(response?.address);
    } else if (response?.address && typeof response?.address !== "string") {
        return response?.address;
    }
};

export const getOrderFlowType = (reviewedCart: ReviewedCart | null): string =>
    reviewedCart?.data?.user_flow_info?.flow_type || "";

export const getIrctcIdentifier = (reviewedCart: ReviewedCart | null): IIrctcPayload | undefined =>
    reviewedCart?.data?.irctc_data;

export const getIrctcTrainOrderAttributes = (
    reviewedCart: ReviewedCart | null,
): IIrctcTrainOrderAttributes | undefined => reviewedCart?.data?.irctcTrainOrderAttributes;

export const getCouponListingVersion = (cartResponse: ReviewedCart | null): COUPON_LISTING_VERSION => {
    return getExperimentValue(cartResponse, EXPERIMENTS.COUPON_LISTING_VERSION) === BOOLEAN_AS_STRING.TRUE
        ? COUPON_LISTING_VERSION.V2
        : COUPON_LISTING_VERSION.DEFAULT;
};

export const isNewUser = (cartResponse: ReviewedCart | null): boolean => cartResponse?.data?.is_new_user ?? false;

export const getQFoodSLA = (cartResponse: ReviewedCart | null): string => {
    if (cartResponse) {
        const restaurantId = getRestaurantId(cartResponse);
        const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode) || "";
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        if (
            qFoodRestaurantsList.includes(restaurantId) &&
            isVersionCodeEligible(MIN_ANDROID_VERSION_Q_FOOD, MIN_IOS_VERSION_Q_FOOD, versionCode, platform)
        ) {
            return Q_FOOD_SLA;
        }
    }
    return "";
};

/**
 * Checks whether the menu page should be refreshed or not depending on the status code (error)
 * @param statusCode Cart Status code - From cartResponse
 * @returns boolean
 */
export const shouldMenuRefresh = (statusCode: CART_STATUS_CODES | undefined): boolean =>
    statusCode ? Boolean(MENU_REFRESH_CART_STATUS_CODES[statusCode]) : false;

export const getUserSpecificConfig = (cartResponse: ReviewedCart | null, key: string): unknown | undefined => {
    return cartResponse?.data?.userSpecificConfigurations?.[key];
};

export const isFoodPageRedirectionApplicable = () => {
    let iosDefaultVersionForFoodPageRedirection;
    if (typeof window !== "undefined") {
        iosDefaultVersionForFoodPageRedirection =
            window?.__MAX_IOS_VERSION_FOR_MENU_REDIRECTION__ || DEFAULT_MAX_IOS_VERSION_FOR_FOOD_PAGE_REDIRECTION;
    } else {
        iosDefaultVersionForFoodPageRedirection = DEFAULT_MAX_IOS_VERSION_FOR_FOOD_PAGE_REDIRECTION;
    }

    const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode) || "";
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    return isVersionCodeEligible(undefined, Number(iosDefaultVersionForFoodPageRedirection), versionCode, platform);
};

export const getRenderingDetailsItemsPosition = (cartResponse: ReviewedCart, key: string): string => {
    const renderingDetails = cartResponse?.data?.rendering_details;

    const keyPosition = renderingDetails?.findIndex((item) => item.key === key);

    return String(keyPosition);
};

export const getCrossSellM2RequestBodyItems = (items: CartItemsList["data"][]): CrossSellBodyItem[] => {
    return items.map((el) => {
        return {
            id: String(el.menu_item_id),
        };
    });
};

export const getPlatform = (platform: string) => {
    switch (platform) {
        case "Swiggy-Android":
            return "PLATFORM_ANDROID";
        case "Swiggy-iOS":
            return "PLATFORM_IOS";
        case "Swiggy-Mweb":
            return "PLATFORM_WEB";
        case "Swiggy-Dweb":
            return "PLATFORM_DWEB";
        default:
            return "PLATFORM_INVALID";
    }
};

export const navigateToDeeplinkGenericHelper = (link: string): void => {
    if (isValidDeeplink(link)) {
        getBridgeSdk().navigateToScreen({
            type: NavigationTypes.DEEPLINK,
            link,
        });
    } else {
        captureError(new Error(`Invalid deeplink in navigateToDeeplinkGenericHelper : ${link}`));
    }
};

export const navigateToHomePage = (): void => {
    // For Ixigo flow, close webview directly instead of navigating
    if (isIxigoFlow()) {
        closeIxigoWebview();
        return;
    }

    getBridgeSdk()
        .navigateToScreen({
            type: NavigationTypes.SCREEN,
            link: NavigationScreenLinkTypes.HOME,
        })
        .catch(() => void 0);
};

export const getRetainedCartOrderId = (): string | null => {
    const orderId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.retainedCartOrderId);
    return orderId;
};

export const getCrossSellPocketHeroNudge = (
    cartResponse: ReviewedCart | null,
): { crossSellNudge: DeliverySpeedNudge | undefined; isCashback: boolean } => {
    if (
        cartResponse?.data?.XPData?.show_ph_nudge_in_cross_sell === BOOLEAN_AS_STRING.TRUE ||
        cartResponse?.data?.XPData?.show_ph_nudge_in_cross_sell_m4 === BOOLEAN_AS_STRING.TRUE
    ) {
        return {
            crossSellNudge: cartResponse?.data?.presentation_attributes_keys?.nudge,
            isCashback: cartResponse?.data?.XPData?.show_ph_nudge_in_cross_sell_m4 === BOOLEAN_AS_STRING.TRUE,
        };
    }
    return { crossSellNudge: undefined, isCashback: false };
};

export const isCartIdSameAsPreviousCartId = (cartId: string) => cartId === Storage.getItem(PREVIOUS_CART_ID);
export const isRestaurantSameAsPrevious = (restaurantId: string) => {
    const prevRxId = LocalStorage.getItem(LOCAL_STORAGE_KEYS.PREVIOUS_RESTAURANT_ID);

    return !isEmpty(prevRxId) && !isEmpty(restaurantId) ? restaurantId === prevRxId : false;
};

export const storeRestaurantId = (restaurantId: string | null) => {
    if (!restaurantId) {
        return;
    }
    const prevRxId = LocalStorage.getItem(LOCAL_STORAGE_KEYS.PREVIOUS_RESTAURANT_ID);
    if (!prevRxId || (!isEmpty(prevRxId) && prevRxId !== String(restaurantId)))
        LocalStorage.setItem(LOCAL_STORAGE_KEYS.PREVIOUS_RESTAURANT_ID, String(restaurantId));
};

export const getOverlayVisibility = (): {
    boltPopup?: boolean;
    boltHalfcard?: boolean;
    ecoSaverLottie?: boolean;
} => {
    return LocalStorage.getItem(LOCAL_STORAGE_KEYS.OVERLAY_VISIBILITY) ?? {};
};

export const storeOverlayVisibility = ({
    boltPopup,
    boltHalfcard,
    ecoSaverLottie,
}: {
    boltPopup?: boolean;
    boltHalfcard?: boolean;
    ecoSaverLottie?: boolean;
}) => {
    const existing = getOverlayVisibility();
    const updated = {
        ...existing,
        ...(boltPopup !== undefined && { boltPopup }),
        ...(boltHalfcard !== undefined && { boltHalfcard }),
        ...(ecoSaverLottie !== undefined && { ecoSaverLottie }),
    };

    LocalStorage.setItem(LOCAL_STORAGE_KEYS.OVERLAY_VISIBILITY, updated);
};

export const handleAddMoreItemsClick = (payload: {
    isGroupCartFlow: boolean;
    isFromBottomBar: boolean;
    groupCartState?: GroupOrderState;
    cartResponse?: ReviewedCart | null;
}) => {
    const { isGroupCartFlow, isFromBottomBar, groupCartState, cartResponse } = payload;
    // Since menu variant is different for group order flow and normal flow, menu redirection also need to be updated here
    if (isGroupCartFlow) {
        if (isFromBottomBar) {
            const restaurantId = groupCartState?.data?.restaurant_details?.restaurant_id || "";
            const groupCartId = groupCartState?.data?.group_cart_id || "";
            const hostName = getHostFirstName(groupCartState?.data) || "";

            getBridgeSdk()
                .navigateToScreen({
                    type: NavigationTypes.DEEPLINK,
                    link: format(GROUP_ORDER_MENU_DEEPLINK, restaurantId, groupCartId, hostName),
                    params: {
                        shouldCloseWebview: true,
                    },
                })
                .catch(() => void 0);
            return;
        }
        closeWebview();
    } else {
        if (cartResponse) {
            getBridgeSdk()
                .navigateToScreen({
                    type: NavigationTypes.SCREEN,
                    link: NavigationScreenLinkTypes.MENU,
                    params: {
                        id: getRestaurantId(cartResponse),
                        fromCart: true,
                    },
                })
                .catch(() => void 0);
        }
    }
};

export const getFreeDelCommsStyling = (
    cartRensponse: ReviewedCartApiResponse | null | undefined,
): FreeDelCommsStyling | undefined => {
    return getNestedValue(cartRensponse, "presentation_attributes_keys.free_del_comms_styling");
};

export const getMenuCalloutInfo = (
    cartResponse: ReviewedCart | null,
): { isAvailable: boolean; info?: MenuItemsCalloutInfo } => {
    return {
        isAvailable: !isEmpty(cartResponse?.data?.presentation_attributes_keys?.menu_items_callout?.message),
        info: getNestedValue(cartResponse, "data.presentation_attributes_keys.menu_items_callout"),
    };
};

export const getAddressNudgePresentation = (
    cartResponse: ReviewedCart | null,
): AddressNudgePresentation | undefined => {
    return cartResponse?.data?.presentation_attributes_keys?.cart_address_nudge_presentation_keys;
};

export const isPORV2Flow = (porXpValue: PorFlowXP) => {
    return [PorFlowXP.POR_V1_ENABLE, PorFlowXP.POR_V2_ENABLE].includes(porXpValue);
};

export const getGrandTotalFromRenderingDetails = (cartResponse: ReviewedCart | null): string => {
    const billHeadingChildren = cartResponse?.data?.rendering_details?.find(
        (item) => item.key === "bill_heading",
    )?.children;
    const grandTotal = billHeadingChildren?.find((item) => item.key === "grand_total")?.value;
    return grandTotal || "";
};

export const getCartItemsFromResponse = (cartResponse: ReviewedCart | null): CartItem[] => {
    if (!isEmpty(cartResponse?.data?.cart_menu_items)) {
        return cartResponse?.data?.cart_menu_items || [];
    }
    return [];
};

export const getSnaccStorefrontV2 = (cartResponse: ReviewedCart | null): boolean => {
    const snaccStorefrontV2Xp = getExperimentValue(cartResponse, EXPERIMENTS.SNACC_STOREFRONT_V2);
    return snaccStorefrontV2Xp === BOOLEAN_AS_STRING.TRUE;
};

/**
 * Clears cart storage for mweb platform when last item is removed
 * Uses web-bridge-sdk storage API to ensure proper clearing
 */
export const clearMwebCartStorage = async (): Promise<void> => {
    if (typeof window === "undefined") {
        return;
    }

    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
    if (!isMWebPlatform(platform)) {
        return;
    }

    try {
        await getBridgeSdk().storage({
            type: STORAGE_TYPES.DELETE,
            data: {
                [WEB_FOOD_CART_LOCAL]: "",
                [SWGY_PWA_CART_DATA]: "",
            },
        });
    } catch (error) {
        captureError(error);
    }
};

export const getOverlayHeaderBannerData = (cartResponse: ReviewedCart | null) => {
    return cartResponse?.data?.cart_header_widget_presentation?.overlay_header_banner;
};

export const hasCartSplashLottie = (cartResponse: ReviewedCart | null): boolean =>
    !isEmpty(cartResponse?.data?.cart_splash_lottie);
